import { StatusBar, Modal, PrimaryButton, SecondaryButton, SnackbarStatus } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './EditOptionGroupDetailsModal.module.scss';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { OptionGroupDetails } from '../CreateOptionGroupModal/OptionGroupDetails/OptionGroupDetails';
import { useForm } from 'react-hook-form';
import { IOptionGroupItem, IOptionGroupValues } from '../CreateOptionGroupModal/types';
import { IMenuItem, IOptionGroup } from '../../types/menu.types';
import { updateModifierGroup } from '../../api/updateModifierGroup/updateModifierGroup';
import { QueryKeys } from '../../hooks/QueryKeys';
import { Status } from '../../api/constants';
import { useMemo, useState } from 'react';
import { OptionGroupPricing } from '../OptionGroupPricing';
import { useSelectedPriceScheme } from '../../hooks/useSelectedPriceScheme/useSelectedPriceScheme';
import { useQueryClient } from '@tanstack/react-query';
import { formatUpdateOptionGroupRequestBody, getOptionGroupDefaultValues } from 'src/helpers/optionGroupHelpers';

interface EditOptionGroupDetailsModalContentProps {
  item: IMenuItem;
  optionGroup: IOptionGroup;
  onClose: () => void;
}

interface EditOptionGroupDetailsModalProps extends EditOptionGroupDetailsModalContentProps {
  isOpen: boolean;
}

const haveNewModifiersBeenAdded = (original: IOptionGroupItem[], current: IOptionGroupItem[]) => {
  if (current.length > original.length) return true;
  const originalIds = new Set(original.map((item) => item.parentItemId));
  return current.some((item) => !originalIds.has(item.parentItemId));
};

const Content = ({ item, optionGroup, onClose }: EditOptionGroupDetailsModalContentProps) => {
  const [showPrices, setShowPrices] = useState<boolean>(false);
  const { selectedPriceSchemeId } = useSelectedPriceScheme();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const queryClient = useQueryClient();
  // Default values can only be set on initial render, so we can safely ignore the dependencies here
  const defaultValues = useMemo(() => getOptionGroupDefaultValues(optionGroup, item), []);
  const useFormReturn = useForm<IOptionGroupValues>({
    defaultValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    watch,
    formState: { isSubmitting, isValid },
  } = useFormReturn;
  const watchModifiers = watch('modifiers');
  const containsNewModifiers = haveNewModifiersBeenAdded(defaultValues.modifiers, watchModifiers);

  const onSubmit = async (data: IOptionGroupValues) => {
    clearError();
    if (containsNewModifiers && !showPrices) {
      setShowPrices(true);
      return;
    }

    try {
      const updateOptionGroupRequestBody = formatUpdateOptionGroupRequestBody(data);
      const response = await updateModifierGroup(
        item.itemId,
        optionGroup.optionGroupId,
        updateOptionGroupRequestBody,
        selectedPriceSchemeId,
      );
      if (response.status !== Status.SUCCESS) {
        throw new Error();
      }
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.item, item.itemId] });
      onClose();

      showNotification({ status: SnackbarStatus.SUCCESS, message: 'Your changes have been successfully saved' });
    } catch {
      showNotification({ status: SnackbarStatus.ERROR, message: 'Something went wrong whilst updating the group' });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {!showPrices ? (
        <OptionGroupDetails useFormReturn={useFormReturn} itemType={item.type} optionGroup={optionGroup} />
      ) : (
        <OptionGroupPricing formReturn={useFormReturn} />
      )}
      <ButtonContainer
        left={
          <SecondaryButton
            size="full-width"
            buttonStyle="outline"
            onClick={() => (showPrices ? setShowPrices(false) : onClose())}
            type="button"
          >
            {showPrices ? 'Back' : 'Close'}
          </SecondaryButton>
        }
        right={
          <PrimaryButton size="full-width" type="submit" key="save" loading={isSubmitting} disabled={!isValid}>
            {containsNewModifiers && !showPrices ? 'Next' : 'Save'}
          </PrimaryButton>
        }
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};

export const EditOptionGroupDetailsModal = ({
  isOpen,
  onClose,
  item,
  optionGroup,
}: EditOptionGroupDetailsModalProps) => {
  return (
    <Modal open={isOpen} closeModal={() => onClose()}>
      <Content item={item} optionGroup={optionGroup} onClose={onClose} />
    </Modal>
  );
};
